import { useEffect, useState } from "react";

import { useSupplierPreview } from "@/hooks/queries/useSupplierPreview";
import type { Department } from "@/types/domain";
import { InlineError } from "../OnboardingShell";
import { OnboardingAlertsStep } from "../OnboardingAlertsStep";
import { ProductStep } from "../ProductStep";
import { ZoneStep } from "../ZoneStep";
import { capitalize, zoneLabel } from "../lineHelpers";
import type { OnboardingSession } from "../useOnboardingSession";
import { useProductSelection } from "../useProductSelection";
import { LiveCard } from "./LiveCard";
import { ResultStep } from "./ResultStep";
import { SearchingStep } from "./SearchingStep";
import {
  SUPPLIER_AWARD_WINDOW_DAYS,
  SUPPLIER_STAGES,
  SUPPLIER_STARTERS,
} from "./supplierLine";

// Que la espera no parpadee: menos que esto se lee como un error de pantalla.
const MIN_SEARCHING_MS = 1500;

export type SupplierStep = "product" | "zone" | "searching" | "result" | "alerts";

/**
 * Flujo del proveedor: qué vende, dónde, el primer resultado con las obras
 * contratadas que lo piden y los avisos. La línea se guarda al salir de la zona.
 */
export function SupplierFlow({
  session,
  departments,
  onStepChange,
  onExit,
}: {
  session: OnboardingSession;
  departments: Department[];
  onStepChange: (step: SupplierStep) => void;
  /** Vuelve al selector de "¿Cómo vas a usar LicitiGo?". */
  onExit: () => void;
}) {
  const selection = useProductSelection();

  const [step, setStep] = useState<SupplierStep>("product");
  const [departmentIds, setDepartmentIds] = useState<number[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lineSaved, setLineSaved] = useState(false);
  const [searchStartedAt, setSearchStartedAt] = useState(0);

  useEffect(() => onStepChange(step), [step]);

  const asking = step === "product" || step === "zone";
  const livePreview = useSupplierPreview({
    terms: selection.terms,
    departmentIds,
    enabled: asking,
  });
  const resultPreview = useSupplierPreview({
    terms: selection.terms,
    departmentIds,
    details: true,
    enabled: !asking,
  });
  const resultReady =
    !resultPreview.isFetching && (resultPreview.isSuccess || resultPreview.isError);
  const zone = zoneLabel(departments, departmentIds);

  // La espera termina cuando la línea quedó guardada y llegó el resultado.
  useEffect(() => {
    if (step !== "searching" || !lineSaved || !resultReady) return;
    const wait = Math.max(0, MIN_SEARCHING_MS - (Date.now() - searchStartedAt));
    const timer = setTimeout(() => setStep("result"), wait);
    return () => clearTimeout(timer);
  }, [step, lineSaved, resultReady, searchStartedAt]);

  /** Guarda la línea del proveedor y arranca la búsqueda del primer resultado. */
  async function saveSupplierLine(zoneIds = departmentIds) {
    setSaveError(null);
    setLineSaved(false);
    setSearchStartedAt(Date.now());
    setStep("searching");
    try {
      await session.saveLine({
        name: selection.lineName,
        data: {
          searchprofile_departmentids: zoneIds,
          searchprofile_stages: SUPPLIER_STAGES,
          searchprofile_contratadomaxdays: SUPPLIER_AWARD_WINDOW_DAYS,
        },
        // Le vende al que ejecuta: lo que vende va como recursos de la obra.
        resources: selection.terms,
      });
      setLineSaved(true);
    } catch {
      setSaveError("No pudimos guardar tu línea de negocio. Inténtalo de nuevo.");
      setStep("zone");
    }
  }

  if (asking) {
    return (
      // En celular la tarjeta va debajo de la pregunta; en escritorio, a la derecha.
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-12">
        <div>
          {step === "product" ? (
            <ProductStep
              segment="supplier"
              tone="emerald"
              eyebrow="Pregunta 1 de 2"
              title="¿Qué vendes?"
              lead="Escríbelo como lo dirías. La IA lo encuentra dentro de los expedientes técnicos de cada obra."
              placeholder="Ej. acero corrugado"
              starters={SUPPLIER_STARTERS}
              termsLabel="La IA lo buscará en los expedientes como"
              products={selection.products}
              onAddProducts={selection.add}
              onRemoveProducts={selection.remove}
              onNext={() => setStep("zone")}
              onBack={onExit}
              onEscape={() => session.escape("supplier", step)}
            />
          ) : (
            <ZoneStep
              tone="emerald"
              eyebrow="Pregunta 2 de 2"
              title="¿Dónde vendes?"
              lead="Los departamentos donde puedes despachar. Elige todos los que quieras."
              nextLabel="Ver quién está comprando"
              departments={departments}
              departmentIds={departmentIds}
              onChange={setDepartmentIds}
              onNext={() => saveSupplierLine()}
              onBack={() => setStep("product")}
            />
          )}
          {saveError && (
            <InlineError
              message={saveError}
              onEscape={session.configured ? undefined : () => session.escape("supplier", step)}
            />
          )}
        </div>
        <div>
          <LiveCard
            productsLabel={selection.products.length > 0 ? selection.headline : null}
            headline={selection.headline}
            zone={capitalize(zone)}
            preview={livePreview}
          />
        </div>
      </div>
    );
  }

  if (step === "searching") {
    return (
      <SearchingStep
        headline={selection.headline}
        lineSaved={lineSaved}
        resultReady={resultReady}
      />
    );
  }

  if (step === "result") {
    return (
      <ResultStep
        headline={selection.headline}
        zone={zone}
        preview={resultPreview.data}
        previewFailed={resultPreview.isError}
        onContinue={() => setStep("alerts")}
        onBack={() => setStep("zone")}
        onSearchAllPeru={
          departmentIds.length > 0
            ? () => {
                setDepartmentIds([]);
                saveSupplierLine([]);
              }
            : undefined
        }
        onChangeProduct={() => setStep("product")}
      />
    );
  }

  return (
    <OnboardingAlertsStep
      tone="emerald"
      title={`Te avisamos cuando una obra con ${selection.headline} firme contrato.`}
      isFree={session.isFree}
      onFinish={() => session.finish("supplier")}
      finishing={session.finishing}
    />
  );
}
